import _ from 'lodash'
import {
  shouldRedact,
  truncateRequestBody,
  truncateResponseBody,
  isReadRequest,
  stripAuthHeader,
  MAX_BODY_REQUEST,
  MAX_BODY_RESPONSE,
  networkRateLimiter,
} from './redaction-rules'
import { getErrorMessage } from '../errors'

type NetworkRequest = {
  reqId: string
  url?: string
  method?: string
  headers?: Record<string, string | null | undefined>
  body?: string
}

type NetworkResponse = {
  reqId: string
  url?: string
  body?: string
}

type WithoutBody<T> = Omit<T, 'body'> & { body: null }

const withoutBody = <T extends { body?: string }>(record: T): WithoutBody<T> => ({
  ..._.omit(record, 'body'),
  body: null,
})

const MAX_TRACKED_REQUESTS = 1000

const reqIdsAllowedResponseBody = new Set<string>()

export function responseSanitizer<T extends NetworkResponse>(
  response: T
): T | WithoutBody<T> | null {
  const isBodyAllowed = reqIdsAllowedResponseBody.delete(response.reqId)

  if (shouldRedact(response.url)) {
    return null
  }

  if (!isBodyAllowed) {
    return withoutBody(response)
  }

  const bodyLength = _.get(response, 'body.length', 0)
  if (bodyLength > MAX_BODY_RESPONSE) {
    try {
      console.log(`Truncating response payload of ${response.body?.length} for ${response.url}`)
      // A slow operation, but at least memory resident
      return {
        ...response,
        body: truncateResponseBody(response.body),
      }
    } catch (err) {
      console.log(`Failed to parse request body: ${getErrorMessage(err)}`)
      return withoutBody(response)
    }
  }
  return response
}

export function requestSanitizer<T extends NetworkRequest>(request: T): T | WithoutBody<T> | null {
  try {

    if (shouldRedact(request.url)) {
      return null
    }

    // Only rate-limit "read" requests like queries or fetches.
    // "Write" requests are important, so we don't rate-limit them.
    const isOverLimit = isReadRequest(request) && !networkRateLimiter()

    stripAuthHeader(request.headers)

    reqIdsAllowedResponseBody.delete(request.reqId)
    if (isOverLimit) {
      return withoutBody(request)
    }

    reqIdsAllowedResponseBody.add(request.reqId)
    for (const oldest of reqIdsAllowedResponseBody) {
      if (reqIdsAllowedResponseBody.size <= MAX_TRACKED_REQUESTS) break
      reqIdsAllowedResponseBody.delete(oldest)
    }

    const bodyLength = _.get(request, 'body.length', 0)
    if (bodyLength >= MAX_BODY_REQUEST) {
      console.log(
        `Truncating request payload of ${request.body?.length} for ${request.method}:${request.url}`
      )
      // This might take a while
      return {
        ...request,
        body: truncateRequestBody(request.body, request.method),
      }
    }

    return request
  } catch (e) {
    console.log(`Failed to parse request body: ${getErrorMessage(e)}`)
    // There may still be a password in there
    return withoutBody(request)
  }
}
